const ACTIVATED_KEY = "omnara:activated";

export type ActivationStep =
  | "activation_start"
  | "cli_install_copy"
  | "cli_run_copy"
  | "session_launch"
  | "message_send";

export type ActivationStatus = "started" | "succeeded" | "blocked" | "pending" | "failed";

export type ActivationPanel = "empty_state" | "new_session" | "chat_new_session";

export interface ActivationStepReachedEvent {
  step: ActivationStep;
  status: ActivationStatus;
  reason?: string;
  user_session_id?: string;
  agent_session_id?: string;
  workspace_id?: string;
  machine_id?: string;
  panel?: ActivationPanel;
  message_length?: number;
  is_websocket_connected?: boolean;
  $set_once?: Record<string, unknown>;
}

type ActivationTrackFn = (
  eventName: "activation_step_reached",
  properties: ActivationStepReachedEvent
) => void;

export type ActivationStepOptions = Omit<
  ActivationStepReachedEvent,
  "step" | "status" | "$set_once"
>;

function isActivated(): boolean {
  try {
    return localStorage.getItem(ACTIVATED_KEY) === "1";
  } catch {
    return false;
  }
}

function markActivated(): void {
  try {
    localStorage.setItem(ACTIVATED_KEY, "1");
  } catch {
    // Never block product flow for analytics.
  }
}

export function clearActivationFlag(): void {
  try {
    localStorage.removeItem(ACTIVATED_KEY);
  } catch {
    // Never block product flow for analytics.
  }
}

export function trackActivationStep(
  track: ActivationTrackFn,
  step: ActivationStep,
  status: ActivationStatus,
  options: ActivationStepOptions = {}
): void {
  if (isActivated()) return;

  const setOnce: Record<string, unknown> = {};
  if (status === "succeeded") {
    setOnce[`activation_${step}_at`] = new Date().toISOString();
  }
  track("activation_step_reached", {
    step,
    status,
    ...options,
    ...(Object.keys(setOnce).length > 0 ? { $set_once: setOnce } : {}),
  });

  if (step === "message_send" && status === "succeeded") {
    markActivated();
  }
}
